/* Zeger van Olden — fullscreen photo viewer for product pages.

   Every photo on a product page (the large hero shot and each thumbnail
   in the grid below it) is wrapped in a ".product__media-trigger" button.
   Clicking or tapping one opens this single, shared viewer at that photo,
   at a higher resolution than the on-page thumbnail.

   Zoom, three ways, so it works the same on a laptop and a phone:
   - click/tap the photo to zoom in on that spot, click/tap again to zoom
     back out (this is what the magnifying-glass button in the corner
     also does, from the photo's centre)
   - scroll/trackpad wheel to zoom in and out under the cursor
   - pinch with two fingers to zoom in and out under the pinch point
   Once zoomed, drag (mouse or finger) to pan around the photo. At 1x,
   a left/right swipe (or the arrow buttons / arrow keys) moves to the
   next photo. */

(function () {
  'use strict';

  var ICON_CLOSE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M5 5l14 14M19 5L5 19"/></svg>';
  var ICON_ZOOM = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.3 15.3L21 21M8 10.5h5M10.5 8v5"/></svg>';
  var ICON_PREV = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M15 5l-7 7 7 7"/></svg>';
  var ICON_NEXT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M9 5l7 7-7 7"/></svg>';

  var MAX_SCALE = 4;
  var CLICK_SCALE = 2.5;
  var TAP_MOVE_TOLERANCE = 8; // px of movement still counted as a click/tap, not a drag
  var SWIPE_THRESHOLD = 50;   // px of horizontal movement (at scale 1) that counts as a swipe

  var gallery = [];        // [{full, alt}]
  var currentIndex = 0;
  var lb, stage, imgEl, closeBtn, prevBtn, nextBtn, zoomBtn, counterEl;

  var scale = 1, tx = 0, ty = 0;
  var pointers = {};                 // active pointers, by id: {x,y}
  var pinchStartDist = 0, pinchStartScale = 1, pinchStartTx = 0, pinchStartTy = 0, pinchMid = null;
  var dragStart = null;              // {x,y,tx,ty} for a single-pointer pan/swipe
  var dragMoved = false;

  function buildGallery() {
    var triggers = document.querySelectorAll('.product__media-trigger');
    gallery = [];
    triggers.forEach(function (trigger, i) {
      var img = trigger.querySelector('img');
      if (!img) return;
      gallery.push({
        full: trigger.getAttribute('data-full') || img.currentSrc || img.src,
        alt: img.getAttribute('alt') || ''
      });
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        open(i);
      });
    });
  }

  function ensureLightbox() {
    if (lb) return;
    lb = document.createElement('div');
    lb.className = 'lightbox';
    lb.hidden = true;
    lb.innerHTML =
      '<div class="lightbox__stage">' +
        '<img class="lightbox__img" alt="" draggable="false">' +
      '</div>' +
      '<button type="button" class="lightbox__close" aria-label="Close">' + ICON_CLOSE + '</button>' +
      '<button type="button" class="lightbox__zoom" aria-label="Zoom in or out">' + ICON_ZOOM + '</button>' +
      '<button type="button" class="lightbox__prev" aria-label="Previous photo">' + ICON_PREV + '</button>' +
      '<button type="button" class="lightbox__next" aria-label="Next photo">' + ICON_NEXT + '</button>' +
      '<div class="lightbox__counter"></div>';
    document.body.appendChild(lb);

    stage = lb.querySelector('.lightbox__stage');
    imgEl = lb.querySelector('.lightbox__img');
    closeBtn = lb.querySelector('.lightbox__close');
    zoomBtn = lb.querySelector('.lightbox__zoom');
    prevBtn = lb.querySelector('.lightbox__prev');
    nextBtn = lb.querySelector('.lightbox__next');
    counterEl = lb.querySelector('.lightbox__counter');

    closeBtn.addEventListener('click', close);
    prevBtn.addEventListener('click', function () { show(currentIndex - 1); });
    nextBtn.addEventListener('click', function () { show(currentIndex + 1); });
    zoomBtn.addEventListener('click', function () { toggleZoomAt(stageCenter()); });

    document.addEventListener('keydown', function (e) {
      if (lb.hidden) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') show(currentIndex - 1);
      else if (e.key === 'ArrowRight') show(currentIndex + 1);
    });

    stage.addEventListener('pointerdown', onPointerDown);
    stage.addEventListener('pointermove', onPointerMove);
    stage.addEventListener('pointerup', onPointerUp);
    stage.addEventListener('pointercancel', onPointerUp);
    stage.addEventListener('wheel', onWheel, { passive: false });
  }

  function stageCenter() {
    var r = stage.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  /* Re-anchors the image so the point under (px,py) stays under it while
     the scale changes from the current value to next. See the write-up
     in the theme notes: with transform-origin left at its default centre,
     translate(tx,ty) scale(s) puts the image's visual centre at
     stageCenter + (tx,ty), so this is the only place the anchoring math
     needs to live. */
  function zoomTo(next, px, py) {
    next = Math.max(1, Math.min(MAX_SCALE, next));
    var c = stageCenter();
    var relX = px - c.x, relY = py - c.y;
    if (next === 1) {
      tx = 0; ty = 0;
    } else {
      var ratio = next / scale;
      tx = relX - (relX - tx) * ratio;
      ty = relY - (relY - ty) * ratio;
    }
    scale = next;
    clampPan();
    applyTransform();
  }

  function toggleZoomAt(point) {
    zoomTo(scale > 1 ? 1 : CLICK_SCALE, point.x, point.y);
  }

  function clampPan() {
    if (scale <= 1) { tx = 0; ty = 0; return; }
    var stageRect = stage.getBoundingClientRect();
    var w = imgEl.offsetWidth * scale;
    var h = imgEl.offsetHeight * scale;
    var maxX = Math.max(0, (w - stageRect.width) / 2);
    var maxY = Math.max(0, (h - stageRect.height) / 2);
    tx = Math.max(-maxX, Math.min(maxX, tx));
    ty = Math.max(-maxY, Math.min(maxY, ty));
  }

  function applyTransform() {
    imgEl.style.setProperty('--lb-s', scale);
    imgEl.style.setProperty('--lb-tx', tx + 'px');
    imgEl.style.setProperty('--lb-ty', ty + 'px');
    lb.classList.toggle('is-zoomed', scale > 1);
  }

  function onWheel(e) {
    e.preventDefault();
    var delta = -e.deltaY;
    var factor = Math.exp(delta * 0.0018);
    zoomTo(scale * factor, e.clientX, e.clientY);
  }

  function onPointerDown(e) {
    if (e.target === prevBtn || e.target === nextBtn || e.target === closeBtn || e.target === zoomBtn) return;
    /* setPointerCapture can throw (e.g. a pointer id the browser no longer
       considers active); that must never stop us from tracking the pointer
       ourselves, or every gesture below silently stops working. */
    try { stage.setPointerCapture(e.pointerId); } catch (err) {}
    pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
    var ids = Object.keys(pointers);

    if (ids.length === 2) {
      var pts = ids.map(function (id) { return pointers[id]; });
      pinchStartDist = distance(pts[0], pts[1]);
      pinchStartScale = scale;
      pinchStartTx = tx; pinchStartTy = ty;
      pinchMid = midpoint(pts[0], pts[1]);
      dragStart = null;
    } else if (ids.length === 1) {
      dragStart = { x: e.clientX, y: e.clientY, tx: tx, ty: ty };
      dragMoved = false;
    }
  }

  function onPointerMove(e) {
    if (!(e.pointerId in pointers)) return;
    pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
    var ids = Object.keys(pointers);

    if (ids.length === 2 && pinchStartDist) {
      var pts = ids.map(function (id) { return pointers[id]; });
      var dist = distance(pts[0], pts[1]);
      var next = pinchStartScale * (dist / pinchStartDist);
      next = Math.max(1, Math.min(MAX_SCALE, next));
      var ratio = next / pinchStartScale;
      var c = stageCenter();
      tx = (pinchMid.x - c.x) - ((pinchMid.x - c.x) - pinchStartTx) * ratio;
      ty = (pinchMid.y - c.y) - ((pinchMid.y - c.y) - pinchStartTy) * ratio;
      scale = next;
      clampPan();
      applyTransform();
      lb.classList.add('is-panning');
    } else if (ids.length === 1 && dragStart) {
      var dx = e.clientX - dragStart.x;
      var dy = e.clientY - dragStart.y;
      if (Math.abs(dx) > TAP_MOVE_TOLERANCE || Math.abs(dy) > TAP_MOVE_TOLERANCE) dragMoved = true;
      if (scale > 1) {
        tx = dragStart.tx + dx;
        ty = dragStart.ty + dy;
        clampPan();
        applyTransform();
        lb.classList.add('is-panning');
      }
    }
  }

  function onPointerUp(e) {
    var wasSingle = Object.keys(pointers).length === 1;
    var target = e.target;
    var end = { x: e.clientX, y: e.clientY };
    var swipe = null;

    if (wasSingle && dragStart) {
      var dx = end.x - dragStart.x;
      if (scale === 1 && Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(end.y - dragStart.y)) {
        swipe = dx > 0 ? 'prev' : 'next';
      } else if (!dragMoved) {
        if (target === stage) {
          close();
        } else {
          toggleZoomAt(dragStart);
        }
      }
    }

    delete pointers[e.pointerId];
    if (Object.keys(pointers).length < 2) { pinchStartDist = 0; pinchMid = null; }
    if (Object.keys(pointers).length === 0) { dragStart = null; lb.classList.remove('is-panning'); }

    if (swipe === 'next') show(currentIndex + 1);
    else if (swipe === 'prev') show(currentIndex - 1);
  }

  function distance(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }
  function midpoint(a, b) { return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; }

  function show(index) {
    var n = gallery.length;
    currentIndex = ((index % n) + n) % n;
    var item = gallery[currentIndex];
    scale = 1; tx = 0; ty = 0;
    applyTransform();
    imgEl.src = item.full;
    imgEl.alt = item.alt;
    var multi = n > 1;
    prevBtn.hidden = !multi;
    nextBtn.hidden = !multi;
    counterEl.hidden = !multi;
    if (multi) counterEl.textContent = (currentIndex + 1) + ' / ' + n;
  }

  function open(index) {
    ensureLightbox();
    show(index);
    lb.hidden = false;
    document.body.classList.add('lightbox-open');
    closeBtn.focus();
  }

  function close() {
    if (!lb) return;
    lb.hidden = true;
    document.body.classList.remove('lightbox-open');
    scale = 1; tx = 0; ty = 0;
    applyTransform();
  }

  function init() {
    buildGallery();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
